import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Table,
    Input,
    Button,
    Space,
    Spin, Select
} from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { SearchOutlined } from "@ant-design/icons";
import CommonObj from "../../CommonObj";
import { message } from "../../components/antdMessageBridge";
import { runGridExport } from "../../components/gridExportHelper";
import { useAuthToken } from "../../components/usecontext/AuthContext";
import "./legalBatchService.css";
import { exportToExcel } from '../exporttoexcel/recycleexporttoexcel';
import { encryptTimestamp } from "../../components/commoncsrf/csrf-common";
import "../../components/commonpagination/common-pagination.css";
const ProcessedISBN = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sortedInfo, setSortedInfo] = useState({});
    const [searchFilters, setsearchFilters] = useState({});
    const { accessToken } = useAuthToken();
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const navigate = useNavigate();
    const location = useLocation();
    const filekey = location.state?.fileId || "";
    const { parentState } = location.state || {};
    const searchInput = useRef(null);
    const [isArchive, setIsArchive] = useState(false);

    const handleReset = (dataIndex, clearFilters) => {
        clearFilters();
        setsearchFilters((prev) => {
            const updated = { ...prev };
            delete updated[dataIndex];
            return updated;
        });
        fetchData({
            pagination,
            sorter: sortedInfo,
            filters: { ...searchFilters, [dataIndex]: undefined },
            archiveStatus: isArchive
        });
    };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    style={{ marginBottom: 8, display: "block" }}
                    onPressEnter={() => confirm()}
                />
                <Space>
                    <Button type="primary" size="small" onClick={() => confirm()}>Search</Button>
                    <Button size="small" onClick={() => {
                        handleReset(dataIndex, clearFilters);
                        confirm();
                    }}>Reset</Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />),
        filteredValue: searchFilters[dataIndex] ? [searchFilters[dataIndex]] : null,
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        sorter: false,
        sortOrder: sortedInfo.columnKey === dataIndex ? sortedInfo.order : null
    });

    const columns = [
        {
            title: "ISBN",
            dataIndex: "isbn",
            key: "isbn",
            width: '33%',
            ...getColumnSearchProps("isbn"),
        },
        {
            title: "ISBN 10",
            dataIndex: "isbN10",
            key: "isbN10",
            width: '33%',
            ...getColumnSearchProps("isbN10"),
        },
        {
            title: "ISBN 13",
            dataIndex: "isbN13",
            key: "isbN13",
            width: '33%',
            ...getColumnSearchProps("isbN13"),
        }
    ];

    const getRowClassName = (record, index) => {
        return index % 2 === 0 ? "odd-row" : "even-row";
    };

    const handleTableChange = (newPagination, filters, sorter) => {
        const normalizeFilters = {};
        Object.keys(filters).forEach((key) => {
            if (filters[key] && filters[key].length > 0) {
                normalizeFilters[key] = filters[key][0];
            }
        });

        setsearchFilters(normalizeFilters);
        setSortedInfo(sorter);

        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        }));

        fetchData({
            pagination: { current: newPagination.current, pageSize: newPagination.pageSize },
            sorter,
            filters: normalizeFilters, archiveStatus: isArchive
        });
    };

    const handleExport = async () => {
        const payload = {
            pageNo: 1,
            pageSize: 0,
            search: JSON.stringify(searchFilters),
            orderByColumn: "",
            orderBy: "ASC",
            fileID: filekey
        };
        const myHeaders = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
            "X-CSRF-Token": encryptTimestamp()
        };
        await runGridExport({
            request: fetch(CommonObj.Endpoints.GetProcessedISBNList, {
                method: "POST",
                headers: myHeaders,
                body: JSON.stringify(payload),
            }),
            exportFn: exportToExcel,
            columns,
            filename: 'Processed ISBN',
        });
    };

    const fetchData = async ({ pagination: pag, filters, sorter, archiveStatus = isArchive }) => {
        const payload = {
            pageNo: pag?.current,
            pageSize: pag?.pageSize || 10,
            search: JSON.stringify(filters),
            orderByColumn: sorter?.field || "",
            orderBy: sorter?.order === "ascend" ? "ASC" : "DESC",
            fileID: filekey,
            isArchive: archiveStatus
        };
        setLoading(true);
        try {
            const myHeaders = {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
                "X-CSRF-Token": encryptTimestamp(),
            };
            const response = await fetch(CommonObj.Endpoints.GetProcessedISBNList, {
                method: "POST",
                headers: myHeaders,
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error("Network response was not ok");
            }
            const responseText = await response.text();
            let result;

            try {
                result = JSON.parse(responseText);
            } catch (e) {
                if (responseText.includes("No logs found")) {
                    result = [];
                } else {
                    throw new Error(responseText || 'Failed to parse API response.');
                }
            }
            const books = Array.isArray(result) ? result : [result];
            const formattedData = books.map((item) => ({
                ...item,
                key: item.isbn,
            }));

            setData(formattedData);
            const total = formattedData[0]?.totalRows || 0;
            setPagination((prev) => ({
                ...prev,
                current: pag?.current || prev.current,
                pageSize: pag?.pageSize || prev.pageSize,
                total,
            }));
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };
    const renderTotal = useCallback((total, range) => {
        return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ marginRight: 8, position: 'absolute', left: '13rem' }}>
                    {range[0]}-{range[1]} of {total} results
                </span>
                <div>
                    <span style={{ marginRight: 4 }}> Go to page:</span>
                    <Select
                        size="small"
                        value={pagination.current || 1}
                        className="custom-page-select"
                        onChange={(page) => {
                            console.log(page, "pagee")
                            const newPagination = { ...pagination, current: page };
                            handleTableChange(
                                newPagination,
                                searchFilters || {},
                                sortedInfo || {}
                            );
                        }}
                    >
                        {Array.from({ length: Math.ceil(total / (pagination.pageSize || 10)) }, (_, i) => (
                            <Select.Option key={i + 1} value={i + 1}>
                                {i + 1}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
            </div>
        );
    }, [pagination.current]);

    useEffect(() => {
        fetchData({ pagination, sorter: sortedInfo, filters: searchFilters, archiveStatus: isArchive });
    }, []);

    const handleArchiveChange = (e) => {
        const newArchiveStatus = e.target.checked;
        setIsArchive(newArchiveStatus);
        const resetPagination = { ...pagination, current: 1 };
        setPagination(resetPagination);
        fetchData({
            pagination: resetPagination, sorter: sortedInfo, filters: searchFilters,
            archiveStatus: newArchiveStatus
        });
    };

    if (loading) {
        return (
            <Spin size="large" style={{ margin: "20px auto" }} />
        );
    }
    const handleGoBack = () => {
        navigate("/legalbatch", { state: parentState });
    };
    return (
        <div className="active-page-container">
            <div className="active-page-header-container">
                <div className="active-page-header-title-container">
                    <h4>Processed ISBN</h4>
                </div>
                <div className="active-page-header-action-container">
                    <div className="book-page-form-checkbox-button" style={{ display: 'flex', alignItems: 'center', marginRight: '16px' }}>
                        <input
                            type="checkbox"
                            id="isArchiveCheckbox"
                            name="isArchiveCheckbox"
                            checked={isArchive}
                            onChange={handleArchiveChange}
                            style={{ cursor: 'pointer', borderRadius: '0.25rem' }}
                        />
                        <label htmlFor="isArchiveCheckbox" style={{ marginLeft: '8px', cursor: 'pointer', fontSize: '14px', color: '#002449' }}>
                            Is Archive Required
                        </label>
                    </div>
                </div>
            </div>
            <div className="active-page-body-container">
                <div className="rounded-table-wrapper">
                    <Table
                        columns={columns}
                        dataSource={data}
                        className="common-pagination common-grid"
                        pagination={{
                            ...pagination,
                            pageSizeOptions: ["10", "20", "30", "40", "50", "100"],
                            showSizeChanger: true,
                            position: ["bottomRight"],
                            defaultPageSize: 10,
                            showTotal: renderTotal
                        }}
                        rowKey={(record) => record.isbn}
                        scroll={{ x: "max-content", y: "calc(100vh - 300px)" }}
                        rowClassName={getRowClassName}
                        onChange={handleTableChange}
                    />
                </div>
            </div>
            <div className="book-page-footer-container">
                <div className="book-page-action-container">
                    <button
                        className="book-page-queue-button"
                        onClick={handleGoBack}
                        type="Button"
                    >
                        <span>
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M9.5 7L4.5 12L9.5 17"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    opacity="0.5"
                                    d="M4.5 12L14.5 12C16.1667 12 19.5 13 19.5 17"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </span>
                        Back
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProcessedISBN;
